'use client';

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { DidomiSDK } from '@didomi/react';
import { getProductSheetLocaleByCountry } from '@repo/common/helpers';
import {
  ThirdPartyService,
  thirdPartyServices,
} from '@/config/third-parties.config';

interface DidomiContextType {
  isReady: boolean;
  scriptsEnabled: string[];
  showPreferences: () => void;
}

interface DidomiProviderProps {
  children: ReactNode;
  consentNoticeId: string;
  country: string;
  enable?: boolean;
  locale: string;
  organizationId: string;
}

const DidomiContext = createContext<DidomiContextType | undefined>(undefined);

export const DidomiProvider = ({
  children,
  consentNoticeId,
  country,
  enable = false,
  locale,
  organizationId,
}: DidomiProviderProps) => {
  const [isReady, setIsReady] = useState(false);
  const [scriptsEnabled, setScriptsEnabled] = useState<string[]>([]);

  const region = useMemo(() => {
    return new Intl.Locale(getProductSheetLocaleByCountry(country, locale))
      ?.region;
  }, [country, locale]);

  const config = useMemo(() => {
    return {
      user: {
        country,
        region,
      },
    };
  }, [country, region]);

  const checkAndInitServices = useCallback(() => {
    const didomiState = globalThis?.window?.didomiState;

    if (!didomiState) {
      return;
    }

    const purposesEnabled = didomiState.didomiPurposesEnabled;

    const thirdPartyServicesEnabled = thirdPartyServices.filter(
      (service: ThirdPartyService) => {
        const hasConsent = purposesEnabled?.includes(service.type);

        if (hasConsent) {
          service.init();
          return true;
        } else {
          service.disable();
          return false;
        }
      },
    );

    setScriptsEnabled(thirdPartyServicesEnabled?.map(({ id }) => id));
  }, [setScriptsEnabled]);

  const handleOnReady = useCallback(() => {
    setIsReady(true);
    checkAndInitServices();
  }, [setIsReady, checkAndInitServices]);

  const handleOnConsentChanged = useCallback(() => {
    checkAndInitServices();
  }, [checkAndInitServices]);

  const showPreferences = () => {
    if (globalThis.window?.Didomi?.preferences?.show) {
      globalThis.window.Didomi.preferences.show();
    } else {
      console.warn('Didomi is not ready');
    }
  };

  const value = useMemo(() => {
    return {
      isReady,
      scriptsEnabled,
      showPreferences,
    };
  }, [isReady, scriptsEnabled, showPreferences]);

  return (
    <DidomiContext.Provider value={value}>
      {enable && (
        <DidomiSDK
          apiKey={organizationId}
          noticeId={consentNoticeId}
          gdprAppliesGlobally={true}
          sdkPath='https://sdk.privacy-center.org/'
          embedTCFStub={true}
          config={config}
          onReady={handleOnReady}
          onConsentChanged={handleOnConsentChanged}
        />
      )}
      {children}
    </DidomiContext.Provider>
  );
};

export const useDidomi = () => {
  const context = useContext(DidomiContext);
  if (!context) {
    throw new Error('Missing DidomiProvider');
  }
  return context;
};
